import { differenceInHours } from "date-fns";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { appRoutes } from "../../AppRoutes";
import cacheConfig from "../../config/cache.config";
import mapConfig from "../../config/map.config";
import { layers } from "../../config/map.constants";
import { store } from "../../store";
import { setCaching, updateBurnsLastCached, updateSSALastCached, updateWeatherLastCached } from "../../store/cache.slice";
import { fetchAndCacheBurns } from "../burns.service";
import { cacheUserLocation, getUserCurrentLocation } from "../location.service";
import { queryGeoJsonFeatureByLonLat } from "../map.service";
import { fetchAndCacheSSAs } from "../ssa.service";
import { fetchAndCacheWeatherInfo} from "../weather.service";

export const useCacheHandler = () => {
	const dispatch = useDispatch();
	const isOnline = useSelector(state => state.cache.isOnline);

	// Check if any data in local cache expired and should be re-fetched, every "cacheConfig.checkCacheInterval" minutes.
	// Updates cached user location every minute.
	useEffect(() => {
		const h = setInterval(() => isOnline && fetchAndCacheData(), cacheConfig.checkCacheInterval);
		return () => { clearInterval(h); }
	}, [isOnline]);

	// Fetch all stale and cacheable data and cache it
	useEffect(() => {
		console.log("ONLINE: ", isOnline)
		isOnline && fetchAndCacheData();
	}, [isOnline]);

	// If offline, go back to Select Location to reset location to cached GPS location.
	useEffect(() => {
		if (!isOnline &&
			 (window.location.pathname !== appRoutes.Home) &&
			 (window.location.pathname !== appRoutes.SelectLocation)
			 ) {
				
			window.location.href = appRoutes.SelectLocation;
		}
	}, [isOnline])

	/**
	 * 1. Get user's location and get a square with smokePlumeRadius*2 width and height and user loc on center.
	 * 2. Using user's location:
	 * 	- Cache user's location.
	 * 	- Fetch and cache county feature from map service.
	 * 	- If SSA data is stale, fetch SSA features around user and cache it.
	 * 	- If weather info data is stale, fetch weather info around user and cache it.
	 * 3. If prescribed burns is stale, fetch new burns from API.
	 * 4. Update lastCached times for weather and ssas and burns.
	 */
	const fetchAndCacheData = useCallback(async () => {
		console.log("FETCHING...")
		const location = await getUserCurrentLocation(mapConfig.smokePlumeRadius);
		if (!location) return;
		dispatch(setCaching(true));

		const countyFeature = queryGeoJsonFeatureByLonLat(location.center, layers.arkansas_bound.geojson);
		const countyName = countyFeature ? countyFeature.properties.county_nam : null

		cacheUserLocation({...location, county: countyName});

		const { ssaLastCached, burnsLastCached, weatherLastCached } = store.getState().cache;
		const isSSAStale = differenceInHours(new Date(), new Date(ssaLastCached)) >= cacheConfig.SSAExpire;
		const isWeatherStale = differenceInHours(new Date(), new Date(weatherLastCached)) >= cacheConfig.weatherExpire;
		const isBurnsStale = differenceInHours(new Date(), new Date(burnsLastCached)) >= cacheConfig.burnsExpire;

		const fetchPromises = [];
		if (isSSAStale) {
			fetchPromises.push(
				fetchAndCacheSSAs(location.bounds)
				.then(() => dispatch(updateSSALastCached()))
			);
		}
		if (isWeatherStale) { 
			fetchPromises.push(
				fetchAndCacheWeatherInfo(location.center[0], location.center[1])
				.then(() => dispatch(updateWeatherLastCached()))
			);
		}
		if (isBurnsStale) {
			fetchPromises.push( fetchAndCacheBurns().then(() => dispatch(updateBurnsLastCached())) );
		}

		await Promise.all(fetchPromises);
		console.log("DONE FETCHING...")
		dispatch(setCaching(false));
	}, []);
}

function hoursToMs(hours) { return hours*60*1000*60}